import {
  CREATE_ROOM_PATH,
  HEARTBEAT_INTERVAL_MS,
  LIST_ROOMS_PATH,
  PROTOCOL_VERSION,
  WS_PATH,
  decodeServerMessage,
  encodeMessage,
  normalizeRoomCode,
} from './protocol.js';
import type {
  ClientMessage,
  CombatEvent,
  CreateRoomRequest,
  CreateRoomResponse,
  ListRoomsResponse,
  MatchResult,
  PvpMode,
  RoomListEntry,
  RoomSnapshot,
  ServerMessage,
  TankSnapshot,
} from './protocol.js';
import { resolvePvpApiBase, toWsBase } from './config.js';

/** `welcome` 事件负载：服务器分配的会话信息与房间全量快照。 */
export interface WelcomePayload {
  playerId: string;
  serverTime: number;
  room: RoomSnapshot;
}

/** `match_start` 事件负载：startsAt 为服务器时钟，用 serverNow() 换算倒计时。 */
export interface MatchStartPayload {
  startsAt: number;
  serverTime: number;
  room: RoomSnapshot;
}

/** `relay` 事件负载：房间内其他玩家广播的坦克快照。 */
export interface RelayPayload {
  from: string;
  snapshot: TankSnapshot;
}

/** `relay_event` 事件负载：房间内其他玩家广播的战斗事件。 */
export interface RelayEventPayload {
  from: string;
  event: CombatEvent;
}

/** `player_left` 事件负载。 */
export interface PlayerLeftPayload {
  playerId: string;
}

/** `error` 事件负载：服务器拒绝某次请求（随后通常会关闭连接）。 */
export interface ErrorPayload {
  code: string;
  message: string;
}

/** NetClient 对外事件表：事件名 → 回调负载类型。 */
export interface NetEventMap {
  welcome: WelcomePayload;
  room_update: RoomSnapshot;
  match_start: MatchStartPayload;
  relay: RelayPayload;
  relay_event: RelayEventPayload;
  player_left: PlayerLeftPayload;
  match_end: MatchResult;
  error: ErrorPayload;
  /** WS 非预期关闭（主动 disconnect() 不触发）。 */
  disconnected: void;
}

export type NetEventName = keyof NetEventMap;

type NetCallback<K extends NetEventName> = (payload: NetEventMap[K]) => void;

/** 时钟偏移 EWMA 平滑系数：越小越稳、收敛越慢。 */
const OFFSET_EWMA_ALPHA = 0.2;

/** 从非 2xx 响应中提取人类可读的报错：优先服务端 JSON 的 message 字段。 */
async function readHttpError(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json();
    if (
      typeof body === 'object' &&
      body !== null &&
      'message' in body &&
      typeof (body as { message: unknown }).message === 'string'
    ) {
      return (body as { message: string }).message;
    }
  } catch {
    // 响应体不是 JSON，落到状态码兜底
  }
  return `HTTP ${response.status}`;
}

/**
 * 客户端网络层：封装与 Cloudflare Worker + Durable Object 后端的会话。
 *
 * 职责：
 * - 创建房间走 HTTP `POST {apiBase}/rooms`，进房后全部消息走
 *   WebSocket `{wsBase}/ws/{code}`（地址解析见 config.ts）；
 * - connect() 完成 hello/welcome 握手，之后每 5s 心跳 ping；
 * - 用 pong 估算服务器时钟偏移（RTT/2 校正 + EWMA 平滑），
 *   serverNow() 返回推算的服务器时钟（用于 match_start 倒计时换算）；
 * - 房间/对局消息的发送与分发，事件通过 on/off 订阅（手写迷你 emitter，零依赖）。
 *
 * 所有消息结构以 src/net/protocol.ts 为唯一契约，本模块不做任何协议扩展。
 */
export class NetClient {
  /**
   * 创建房间并直接进房（创建者即房主）。
   *
   * @param mode 对战模式（决定房间容量与对局规则）。
   * @param nick 玩家昵称。
   * @returns 已完成握手的 NetClient 实例；HTTP 非 2xx（带服务端 message）
   *          或 WS 握手失败时 reject。
   */
  static async createRoom(mode: PvpMode, nick: string): Promise<NetClient> {
    const request: CreateRoomRequest = { mode, version: PROTOCOL_VERSION };
    const response = await fetch(`${resolvePvpApiBase()}${CREATE_ROOM_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      throw new Error(await readHttpError(response));
    }
    const data = (await response.json()) as CreateRoomResponse;
    const client = new NetClient();
    await client.connect(data.code, nick);
    return client;
  }

  /**
   * 按 4 位房间码加入已有房间。
   * 房间不存在 / 满员 / 已在进行中等情况由服务端 error 消息体现
   * （'error' 事件 + connect() reject）。
   */
  static async joinRoom(code: string, nick: string): Promise<NetClient> {
    const client = new NetClient();
    await client.connect(code, nick);
    return client;
  }

  /**
   * 拉取大厅房间列表（等待中且未满员的公开房间）。
   * 后端未部署房间列表能力时按错误抛出，由调用方降级提示。
   */
  static async listRooms(): Promise<RoomListEntry[]> {
    const response = await fetch(`${resolvePvpApiBase()}${LIST_ROOMS_PATH}`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      throw new Error(await readHttpError(response));
    }
    const data = (await response.json()) as ListRoomsResponse;
    return Array.isArray(data.rooms) ? data.rooms : [];
  }

  private ws: WebSocket | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  /** 服务器时钟 − 本地时钟（ms），未知时为 null。 */
  private serverTimeOffset: number | null = null;
  private playerIdValue: string | null = null;
  private roomCodeValue = '';
  private nickValue = '';
  /** disconnect() 主动关闭标记：主动关闭不触发 'disconnected'。 */
  private intentionalClose = false;
  /** 最近一次 welcome 负载（握手或重发）；协调器用它拿初始房间快照。 */
  private welcomeValue: WelcomePayload | null = null;
  private readonly listeners = new Map<NetEventName, Set<(payload: never) => void>>();

  /** 服务器分配的 playerId；welcome 之前为 null。 */
  get playerId(): string | null {
    return this.playerIdValue;
  }

  /** 最近一次 welcome 负载；未握手时为 null。 */
  get lastWelcome(): WelcomePayload | null {
    return this.welcomeValue;
  }

  /** 当前所在房间码（已规范化大写）；未连接时为空串。 */
  get roomCode(): string {
    return this.roomCodeValue;
  }

  /** 本次会话使用的昵称。 */
  get nick(): string {
    return this.nickValue;
  }

  /** 是否处于已连接状态（WS OPEN）。 */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /** 最近一次心跳往返时间（ms）；尚无 pong 时为 null。 */
  private lastRttMs: number | null = null;

  /** 最近一次心跳 RTT（ms），HUD 延迟显示用。 */
  get rttMs(): number | null {
    return this.lastRttMs;
  }

  /** 订阅事件；同一回调重复注册会被忽略。 */
  on<K extends NetEventName>(event: K, cb: NetCallback<K>): void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(cb as (payload: never) => void);
  }

  /** 取消订阅事件。 */
  off<K extends NetEventName>(event: K, cb: NetCallback<K>): void {
    this.listeners.get(event)?.delete(cb as (payload: never) => void);
  }

  /**
   * 建立 WS 连接并完成握手。
   *
   * @param code 4 位房间码（内部自动规范化：去空白、大写）。
   * @param nick 玩家昵称（hello.name）。
   * @param rejoinAs 会话恢复时带回的原 playerId（席位还在则恢复，已被移出则按新玩家处理）。
   * @returns welcome 负载（playerId / serverTime / 房间快照）；
   *          连接失败或握手被拒（服务端 error / 提前关闭）时 reject。
   */
  connect(code: string, nick: string, rejoinAs?: string): Promise<WelcomePayload> {
    // 已有连接时先干净地关闭，避免泄漏旧心跳与旧监听器状态。
    if (this.ws) this.disconnect();

    this.roomCodeValue = normalizeRoomCode(code);
    this.nickValue = nick;

    return new Promise<WelcomePayload>((resolve, reject) => {
      const url = `${toWsBase(resolvePvpApiBase())}${WS_PATH}/${this.roomCodeValue}`;
      const ws = new WebSocket(url);
      this.ws = ws;
      this.intentionalClose = false;
      this.serverTimeOffset = null;
      this.playerIdValue = null;
      this.welcomeValue = null;

      let settled = false;

      ws.onopen = () => {
        const hello: ClientMessage = {
          type: 'hello',
          name: nick,
          version: PROTOCOL_VERSION,
          ...(rejoinAs ? { rejoinAs } : {}),
        };
        this.send(hello);
      };

      ws.onmessage = (ev: MessageEvent) => {
        let msg: ServerMessage;
        try {
          msg = decodeServerMessage(String(ev.data));
        } catch {
          return; // 非 JSON 帧直接丢弃，不中断会话
        }
        if (!settled) {
          // 握手阶段：只认 welcome；error / 提前关闭则 reject。
          if (msg.type === 'welcome') {
            settled = true;
            this.playerIdValue = msg.playerId;
            // welcome 的 serverTime 作为偏移初值（无 RTT 校正，随后由 pong 精化）。
            this.serverTimeOffset = msg.serverTime - Date.now();
            this.startHeartbeat();
            const payload: WelcomePayload = {
              playerId: msg.playerId,
              serverTime: msg.serverTime,
              room: msg.room,
            };
            this.welcomeValue = payload;
            this.emit('welcome', payload);
            resolve(payload);
          } else if (msg.type === 'error') {
            settled = true;
            this.emit('error', { code: msg.code, message: msg.message });
            reject(new Error(`${msg.code}: ${msg.message}`));
          }
          return;
        }
        this.dispatch(msg);
      };

      ws.onclose = () => {
        this.stopHeartbeat();
        const wasIntentional = this.intentionalClose;
        this.ws = null;
        this.playerIdValue = null;
        if (!settled) {
          settled = true;
          reject(new Error('connection closed before welcome'));
        }
        if (!wasIntentional) this.emit('disconnected', undefined);
      };

      ws.onerror = () => {
        // onclose 会随后触发，统一在那里收尾；此处仅保证握手期 reject。
        if (!settled) {
          settled = true;
          reject(new Error('websocket error'));
        }
      };
    });
  }

  /** 推算的服务器当前时间（ms）；未知偏移时退化为本地时间。 */
  serverNow(): number {
    return Date.now() + (this.serverTimeOffset ?? 0);
  }

  /** 设置准备状态（房主恒视为已 ready）。 */
  setReady(ready: boolean): boolean {
    return this.send({ type: 'set_ready', ready });
  }

  /** 房主请求开赛（要求满员且全员 ready）。 */
  startGame(): boolean {
    return this.send({ type: 'start_game' });
  }

  /** 离开当前房间（对局结束后「返回大厅」也走这里）。 */
  leaveRoom(): boolean {
    return this.send({ type: 'leave_room' });
  }

  /** 广播自己的坦克快照（playing 阶段 ~15Hz 高频调用，未连接时静默丢弃）。 */
  sendState(snapshot: TankSnapshot): boolean {
    return this.send({ type: 'state', snapshot });
  }

  /** 广播战斗事件（fire/hit/died/mine）。 */
  sendEvent(event: CombatEvent): boolean {
    return this.send({ type: 'event', event });
  }

  /** 主动断开连接并停止心跳；不会触发 'disconnected'。 */
  disconnect(): void {
    this.intentionalClose = true;
    this.stopHeartbeat();
    if (this.ws) {
      const ws = this.ws;
      this.ws = null;
      this.playerIdValue = null;
      // 已 OPEN/CONNECTING 的 socket 直接 close；onclose 里按 intentional 静默。
      try {
        ws.close();
      } catch {
        // 忽略重复 close 等异常
      }
    }
  }

  // -------------------------------------------------------------------------
  // 内部实现
  // -------------------------------------------------------------------------

  /** 发送消息；WS 未处于 OPEN 时静默丢弃并返回 false（高频消息允许丢帧）。 */
  private send(msg: ClientMessage): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    this.ws.send(encodeMessage(msg));
    return true;
  }

  /** 分发握手完成后的服务器消息。 */
  private dispatch(msg: ServerMessage): void {
    switch (msg.type) {
      case 'welcome': {
        // 断线重连场景下服务器可能重发 welcome；直接透传。
        this.playerIdValue = msg.playerId;
        const payload: WelcomePayload = {
          playerId: msg.playerId,
          serverTime: msg.serverTime,
          room: msg.room,
        };
        this.welcomeValue = payload;
        this.emit('welcome', payload);
        break;
      }
      case 'room_update':
        this.emit('room_update', msg.room);
        break;
      case 'match_start':
        this.emit('match_start', {
          startsAt: msg.startsAt,
          serverTime: msg.serverTime,
          room: msg.room,
        });
        break;
      case 'relay':
        this.emit('relay', { from: msg.from, snapshot: msg.snapshot });
        break;
      case 'relay_event':
        this.emit('relay_event', { from: msg.from, event: msg.event });
        break;
      case 'player_left':
        this.emit('player_left', { playerId: msg.playerId });
        break;
      case 'match_end':
        this.emit('match_end', msg.result);
        break;
      case 'error':
        this.emit('error', { code: msg.code, message: msg.message });
        break;
      case 'pong':
        this.handlePong(msg.t, msg.serverTime);
        break;
    }
  }

  /** 启动 5s 心跳；ping 携带本地时间戳用于 RTT 测量。 */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: 'ping', t: Date.now() });
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * 用 pong 估算服务器时钟偏移：offset = serverTime + RTT/2 − now，
   * 再以 EWMA 平滑，避免单次网络抖动拉偏 match_start 倒计时。
   */
  private handlePong(pingT: number, serverTime: number): void {
    const now = Date.now();
    const rtt = Math.max(0, now - pingT);
    this.lastRttMs = rtt;
    const sample = serverTime + rtt / 2 - now;
    this.serverTimeOffset =
      this.serverTimeOffset === null
        ? sample
        : this.serverTimeOffset + OFFSET_EWMA_ALPHA * (sample - this.serverTimeOffset);
  }

  /** 触发事件回调；单个回调异常不影响其余订阅者。 */
  private emit<K extends NetEventName>(event: K, payload: NetEventMap[K]): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const cb of set) {
      try {
        (cb as NetCallback<K>)(payload);
      } catch {
        // 订阅者异常不扩散到网络层
      }
    }
  }
}
